import React from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Filter,
  Search,
  X,
} from 'lucide-react';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { cn } from '@cotera/client/v0/lib/utils';
import type { FilterOption, SortOption } from './resource-list-view-types';

export type ResourceListToolbarProps<T> = {
  /**
   * Hides sort, search and filter. Set when the list is empty *and* nothing is
   * narrowing it — controls for cutting a list down say nothing when there is
   * no list. Deliberately not set when a search or filter produced the empty
   * result: removing them then would strand the user with no way to undo the
   * thing that emptied the view.
   */
  hideNarrowingControls?: boolean;
  hasSortOptions: boolean;
  sortOptions?: SortOption<T>[];
  activeSort: string | undefined;
  sortDirection: 'asc' | 'desc';
  onSortSelect: (key: string) => void;
  searchOpen: boolean;
  searchQuery: string;
  searchPlaceholder: string;
  onSearchToggle: () => void;
  onSearchQueryChange: (value: string) => void;
  onSearchKeyDown: (e: React.KeyboardEvent) => void;
  /** Focus target for the `/` and ⌘F shortcuts owned by the list. */
  searchInputRef?: React.Ref<HTMLInputElement>;
  hasFilterOptions: boolean;
  filterOptions?: FilterOption[];
  primaryAction?: {
    label: string;
    onClick: () => void;
    /**
     * When provided, the `+` button opens a menu of these options instead of
     * firing `onClick` directly (e.g. "New trigger" / "New workflow").
     */
    menuItems?: {
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
    }[];
  };
  toolbarExtras?: React.ReactNode;
};

type NarrowingControlsProps<T> = Pick<
  ResourceListToolbarProps<T>,
  | 'hasSortOptions'
  | 'sortOptions'
  | 'activeSort'
  | 'sortDirection'
  | 'onSortSelect'
  | 'searchOpen'
  | 'searchQuery'
  | 'searchPlaceholder'
  | 'onSearchToggle'
  | 'onSearchQueryChange'
  | 'onSearchKeyDown'
  | 'searchInputRef'
  | 'hasFilterOptions'
  | 'filterOptions'
>;

/**
 * Sort, search and filter: the controls that cut a list down.
 *
 * Their own component so `hideNarrowingControls` is one condition at the call
 * site rather than a copy on each of the three. They are always shown or hidden
 * together — a list with nothing in it has nothing to narrow — so there is no
 * state in which one belongs on the bar without the others.
 */
function NarrowingControls<T>({
  hasSortOptions,
  sortOptions,
  activeSort,
  sortDirection,
  onSortSelect,
  searchOpen,
  searchQuery,
  searchPlaceholder,
  onSearchToggle,
  onSearchQueryChange,
  onSearchKeyDown,
  searchInputRef,
  hasFilterOptions,
  filterOptions,
}: NarrowingControlsProps<T>): React.ReactElement {
  const activeSortOption = sortOptions?.find((o) => o.key === activeSort);
  return (
    <>
      {hasSortOptions && sortOptions !== undefined && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              // The label makes the current sort readable at a glance — an
              // unlabeled ↕ icon left users unable to tell what order they were
              // looking at without opening the menu.
              aria-label={
                activeSortOption !== undefined
                  ? `Sort: ${activeSortOption.label}, ${
                      sortDirection === 'asc' ? 'ascending' : 'descending'
                    }`
                  : 'Sort'
              }
              className={cn(
                'inline-flex min-w-0 shrink items-center justify-center gap-1 h-8 rounded-md px-2',
                'text-muted-foreground hover:text-foreground hover:bg-secondary',
                'transition-colors cursor-pointer text-sm',
                activeSort !== undefined && 'text-foreground'
              )}
            >
              <ArrowUpDown className="h-4 w-4 shrink-0" aria-hidden="true" />
              {activeSortOption !== undefined ? (
                <span className="hidden sm:inline truncate">
                  {activeSortOption.label}
                </span>
              ) : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {sortOptions.map((option) => {
              const isActive = activeSort === option.key;
              return (
                <DropdownMenuItem
                  key={option.key}
                  className="cursor-pointer flex items-center justify-between"
                  onClick={() => onSortSelect(option.key)}
                >
                  <span>{option.label}</span>
                  {isActive &&
                    (sortDirection === 'asc' ? (
                      <ArrowUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ArrowDown className="h-4 w-4 text-muted-foreground" />
                    ))}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* `min-w-0` + `flex-1` while open: the field used to hold a flat 192px
          whatever room the row had, so on a phone — and in the artifact panel,
          which is just as narrow on a desktop — opening search pushed the
          filter and `+` buttons off the end of the bar. It now takes what is
          left, up to the same 192px, and shrinks instead of shoving. */}
      <div className={cn('flex items-center', searchOpen && 'min-w-0 flex-1')}>
        <button
          onClick={onSearchToggle}
          aria-label={searchOpen ? 'Close search' : 'Search'}
          aria-expanded={searchOpen}
          className={cn(
            'inline-flex shrink-0 items-center justify-center h-8 w-8 rounded-md',
            'text-muted-foreground hover:text-foreground hover:bg-secondary',
            'transition-colors cursor-pointer',
            searchOpen && 'text-foreground'
          )}
        >
          <Search className="h-4 w-4" />
        </button>
        {searchOpen && (
          <div className="relative flex min-w-0 flex-1 items-center">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={onSearchKeyDown}
              aria-label={searchPlaceholder}
              className="h-8 text-sm w-full max-w-48 min-w-0 !border-none !shadow-none !rounded-none !ring-0 focus-visible:!ring-0 focus-visible:!shadow-none focus-visible:!border-none px-2 pr-7"
              autoFocus
            />
            {searchQuery !== '' ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => onSearchQueryChange('')}
                className="absolute right-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        )}
      </div>

      {hasFilterOptions && filterOptions !== undefined && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Filter"
              className={cn(
                'inline-flex shrink-0 items-center justify-center h-8 w-8 rounded-md',
                'text-muted-foreground hover:text-foreground hover:bg-secondary',
                'transition-colors cursor-pointer',
                filterOptions.some((o) => o.checked) && 'text-foreground'
              )}
            >
              <Filter className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {filterOptions.map((option, index) => {
              // A heading (and a rule, once past the first group) whenever the
              // section changes, so grouped options read as one control.
              const previousSection = filterOptions[index - 1]?.section;
              const startsSection =
                option.section !== undefined &&
                option.section !== previousSection;
              return (
                <React.Fragment key={option.key}>
                  {startsSection ? (
                    <>
                      {index > 0 ? <DropdownMenuSeparator /> : null}
                      <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
                        {option.section}
                      </DropdownMenuLabel>
                    </>
                  ) : null}
                  <DropdownMenuItem
                    disabled={option.disabled}
                    className={
                      option.disabled === true ? undefined : 'cursor-pointer'
                    }
                    // Keep the menu open on toggle so multiple filters can be set.
                    onSelect={(e) => {
                      e.preventDefault();
                      option.onChange(!option.checked);
                    }}
                  >
                    {option.icon !== undefined ? (
                      <span className="inline-flex text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
                        {option.icon}
                      </span>
                    ) : null}
                    <span className="flex min-w-0 flex-col">
                      <span>{option.label}</span>
                      {option.hint !== undefined ? (
                        <span className="text-[11px] whitespace-normal text-muted-foreground">
                          {option.hint}
                        </span>
                      ) : null}
                    </span>
                    {option.checked ? (
                      <Check className="ml-auto h-4 w-4 shrink-0 self-start" />
                    ) : null}
                  </DropdownMenuItem>
                </React.Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

export function ResourceListToolbar<T>({
  hasSortOptions,
  sortOptions,
  activeSort,
  sortDirection,
  onSortSelect,
  searchOpen,
  searchQuery,
  searchPlaceholder,
  onSearchToggle,
  onSearchQueryChange,
  onSearchKeyDown,
  searchInputRef,
  hasFilterOptions,
  filterOptions,
  primaryAction,
  toolbarExtras,
  hideNarrowingControls = false,
}: ResourceListToolbarProps<T>): React.ReactElement {
  return (
    <div className="flex min-w-0 items-center justify-end gap-1">
      {hideNarrowingControls ? null : (
        <NarrowingControls
          hasSortOptions={hasSortOptions}
          sortOptions={sortOptions}
          activeSort={activeSort}
          sortDirection={sortDirection}
          onSortSelect={onSortSelect}
          searchOpen={searchOpen}
          searchQuery={searchQuery}
          searchPlaceholder={searchPlaceholder}
          onSearchToggle={onSearchToggle}
          onSearchQueryChange={onSearchQueryChange}
          onSearchKeyDown={onSearchKeyDown}
          searchInputRef={searchInputRef}
          hasFilterOptions={hasFilterOptions}
          filterOptions={filterOptions}
        />
      )}

      {toolbarExtras !== undefined && toolbarExtras !== null && (
        <div className="flex shrink-0 items-center gap-1 ml-2">
          {toolbarExtras}
        </div>
      )}

      {primaryAction !== undefined &&
        (primaryAction.menuItems !== undefined &&
        primaryAction.menuItems.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Labelled, not a bare `+` — the icon alone gave sighted users no
                  clue what would be created. The label collapses on narrow
                  viewports, so `aria-label` still carries it there. */}
              <Button
                variant="outline"
                size="sm"
                icon="plus"
                aria-label={primaryAction.label}
                className="ml-2 shrink-0"
              >
                <span className="hidden sm:inline">{primaryAction.label}</span>
              </Button>
            </DropdownMenuTrigger>
            {/* Roomier than the default dense menu: this is a primary creation
                affordance with a handful of choices, not a long action list, so
                it reads better with list-like spacing. Radius is inherited.

                The width only has to clear the longest row — an icon tile and a
                one-word label — so it is set from the content rather than the
                sentence-length labels this menu used to carry. */}
            <DropdownMenuContent align="end" className="min-w-[10rem] p-1">
              {primaryAction.menuItems.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  onClick={item.onClick}
                  // Spacing only. The size is `DropdownMenuItem`'s own `text-sm`,
                  // which every other menu in the app uses — an arbitrary size
                  // here won `cn`'s tailwind-merge and left this the one menu
                  // rendering a half-pixel large.
                  className="cursor-pointer gap-2.5 px-2.5 py-2"
                >
                  {item.icon}
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            onClick={primaryAction.onClick}
            variant="outline"
            size="sm"
            icon="plus"
            aria-label={primaryAction.label}
            className="ml-2 shrink-0"
          >
            <span className="hidden sm:inline">{primaryAction.label}</span>
          </Button>
        ))}
    </div>
  );
}
